/**
 * Widget Context Provider
 *
 * Provides widget configuration and utilities to all child components.
 */

import React, { createContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useSessionKeepAlive } from './hooks/useSessionKeepAlive';
import { useEhrMediator } from './hooks/useEhrMediator';
import { AtlasUIProvider } from '@wellsky/atlas-ui-react';
import { QueryClient, QueryClientProvider, type Query } from '@tanstack/react-query';
import type { ResolvedWidgetConfig, WidgetConfig, WidgetImperativeRef } from './types';
import { resolveConfig } from './utils/configuration';
import { ApiClientContext } from '../contexts/ApiClientContext';
import { createWidgetWorkItemsApi } from './services/widgetWorkItemsApi';
import { useTransientErrorWake } from '../hooks/useTransientErrorWake';

/**
 * Widget context value interface
 */
export interface WidgetContextValue {
  /** Resolved widget configuration */
  config: ResolvedWidgetConfig;
  /** Function to update configuration */
  updateConfig: (newConfig: Partial<WidgetConfig>) => void;
  /** Portal container for Radix UI components */
  portalContainer: ShadowRoot | HTMLElement | null;
  /** Function to trigger data refresh */
  refresh: () => void;
}

/**
 * Widget Context
 */
export const WidgetContext = createContext<WidgetContextValue | null>(null);

/**
 * Portal Context for Radix UI components
 */
export const PortalContext = createContext<ShadowRoot | HTMLElement | null>(null);

interface WidgetProviderProps {
  /** Initial widget configuration */
  config: ResolvedWidgetConfig;
  /** Portal container for Radix UI components (usually shadowRoot) */
  portalContainer?: ShadowRoot | HTMLElement | null;
  /** Imperative ref for external method calls (e.g. setAuthToken) */
  imperativeRef?: WidgetImperativeRef;
  /** Child components */
  children: React.ReactNode;
}

/**
 * Widget Provider Component
 *
 * Wraps the widget content with necessary providers:
 * - WidgetContext for configuration
 * - QueryClientProvider for data fetching
 * - PortalContext for Radix UI portals
 */
export function WidgetProvider({ config: initialConfig, portalContainer, imperativeRef, children }: WidgetProviderProps) {
  const [config, setConfig] = useState<ResolvedWidgetConfig>(initialConfig);

  // Create a stable QueryClient instance
  const queryClientRef = useRef<QueryClient | null>(null);
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: config.features.refreshInterval,
          refetchOnWindowFocus: false,
          retry: 1,
        },
      },
    });
  }

  // A view that is still failing when the user comes back to the page asks once more.
  useTransientErrorWake(queryClientRef.current);

  // --- EHR Mediator integration ---

  // Session keep-alive: send postMessages to parent on user activity
  useSessionKeepAlive({
    enabled: config.enableSessionKeepAlive,
  });

  /**
   * The token the queries use (set on mount and by the token effect below). A host push of
   * this same token does not change the token in config, so that effect does not run for it.
   */
  const prevToken = useRef(config.auth?.token);

  /**
   * The one same-token retry each failed query got: the token and the query's
   * dataUpdateCount at that retry.
   */
  const sameTokenRetriesRef = useRef(new WeakMap<Query, { token: string; dataUpdateCount: number }>());

  /**
   * Refetch the failed queries once when the host pushes the token the widget already holds.
   *
   * After a 401 the old host broker can hand back the same token (it is still valid for the
   * shell). Contract C12 / AC-001.3: every failed view refetches after setAuthToken. Healthy
   * queries are left alone. Each failed query gets one retry per token: if it fails again
   * with that token, a further push of the same token does nothing, so the view stays on its
   * error (for a 401, the final session-expired state) and there is no 401 -> refresh -> 401
   * loop. A success of the query, or a new token, starts a new cycle.
   *
   * The invalidation can run in this tick: the query functions already carry this token.
   * Same rule as the RevenueQueue WidgetProvider.
   */
  const retryFailedQueriesForSameToken = useCallback((token: string) => {
    const client = queryClientRef.current;
    if (!client) return;
    const retries = sameTokenRetriesRef.current;
    const due = client.getQueryCache().findAll({
      predicate: (query) => {
        if (query.state.status !== 'error') return false;
        const last = retries.get(query);
        return !last || last.token !== token || query.state.dataUpdateCount > last.dataUpdateCount;
      },
    });
    if (due.length === 0) return;
    for (const query of due) {
      retries.set(query, { token, dataUpdateCount: query.state.dataUpdateCount });
    }
    const dueSet = new Set<Query>(due);
    void client.invalidateQueries({ predicate: (query) => dueSet.has(query) });
  }, []);

  // Token refresh handler: update auth config when parent sends a token.
  // A new token: the invalidation is handled by the effect below.
  // The same token: only the failed queries refetch, once (see above).
  const handleTokenRefresh = useCallback(
    (newToken: string) => {
      if (newToken === prevToken.current) retryFailedQueriesForSameToken(newToken);
      setConfig((prev) => ({
        ...prev,
        auth: {
          ...prev.auth,
          token: newToken,
        },
      }));
    },
    [retryFailedQueriesForSameToken]
  );

  /**
   * Refetch everything whenever the token changes.
   *
   * Necessary because query keys deliberately exclude the token: a query that
   * already settled into a 401 will never re-issue on its own, so updating the
   * config alone would leave the widget displaying the failure it was stuck on.
   *
   * It has to be an effect rather than a line inside `handleTokenRefresh`.
   * Invalidating in the same tick as `setConfig` refetches through the query
   * functions from the *previous* render, which still close over the old auth --
   * the request goes out again carrying the very token that just expired.
   */
  // The guard compares tokens rather than counting renders. React StrictMode runs this
  // effect twice on mount; a "first run" flag would let the second run invalidate every
  // query on mount. Comparing with the token the queries last used (prevToken) skips both
  // mount runs and a host push of the token the widget already holds (handleTokenRefresh
  // retries the failed queries for that case).
  useEffect(() => {
    if (prevToken.current === config.auth?.token) return;
    prevToken.current = config.auth?.token;
    queryClientRef.current?.invalidateQueries();
  }, [config.auth?.token]);

  // Listen for EHR Mediator messages (token refresh, session expiry)
  useEhrMediator({
    onTokenRefresh: handleTokenRefresh,
    onSessionExpired: config.onSessionExpired,
    parentOrigin: config.auth?.parentOrigin,
  });

  // Wire imperative ref so external callers can update React state
  useEffect(() => {
    if (imperativeRef) {
      imperativeRef.current.setAuthToken = handleTokenRefresh;
    }
    return () => {
      if (imperativeRef) {
        imperativeRef.current.setAuthToken = null;
      }
    };
  }, [imperativeRef, handleTokenRefresh]);

  // No need for portal container setup - it comes from props (shadowRoot)

  // Update configuration
  const updateConfig = useCallback((newConfig: Partial<WidgetConfig>) => {
    setConfig((prev) => resolveConfig({ ...prev, ...newConfig }));
  }, []);

  // Refresh data
  const refresh = useCallback(() => {
    queryClientRef.current?.invalidateQueries();
  }, []);

  // Context value
  const contextValue: WidgetContextValue = {
    config,
    updateConfig,
    portalContainer: portalContainer || null,
    refresh,
  };

  // Create widget-specific API client that uses configured apiBaseUrl
  const widgetApi = useMemo(() => createWidgetWorkItemsApi(config), [config]);

  // API client value for context
  const apiClientValue = useMemo(() => ({
    getWorkItem: async (workItemId: string) => {
      return widgetApi.getWorkItem(workItemId);
    },
    dismissWorkItem: async (workItemId: string, reason?: string) => {
      return widgetApi.dismissWorkItem(workItemId, reason);
    },
  }), [widgetApi, config.apiBaseUrl]);

  return (
    <AtlasUIProvider defaultTheme="light" loadFonts={false}>
      <WidgetContext.Provider value={contextValue}>
        <QueryClientProvider client={queryClientRef.current}>
          <ApiClientContext.Provider value={apiClientValue}>
            <PortalContext.Provider value={portalContainer || null}>
              {children}
            </PortalContext.Provider>
          </ApiClientContext.Provider>
        </QueryClientProvider>
      </WidgetContext.Provider>
    </AtlasUIProvider>
  );
}
